import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AddPatientForm } from '../../ComponnentsUI/features/AddPatientForm';
import { usePatientDossier } from '../../../helpers/api/patients/patients';
import type { Patient } from '../../interfaces/patient';
import { PatientFormPageShell } from './PatientFormPageShell';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { ErrorDisplay } from '../../ComponnentsUI/common/ErrorDisplay';
import { Button } from '../../ComponnentsUI/common/Button';
import {
  unwrapPatientDossier,
  type PatientDossierPayload,
} from './PatientDetailsView';
import { getLoadErrorMessage } from './patientFormPage.utils';

export const EditPatientPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('PatientManagement');
  const { t: tPatient } = useTranslation('Patient');
  const navigate = useNavigate();
  const location = useLocation();
  const editFormRef = useRef<HTMLFormElement>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ??
    (id ? `/patients/${id}` : '/patients');

  const {
    data: dossierResponse,
    isLoading,
    error,
    refetch,
  } = usePatientDossier(id);

  const dossier: PatientDossierPayload = useMemo(
    () => unwrapPatientDossier(dossierResponse),
    [dossierResponse]
  );

  const patientToEdit = useMemo(() => {
    if (!dossier.patient) {
      return null;
    }
    const merged = {
      ...(dossier.patient as Record<string, unknown>),
    };
    if (dossier.pregnancy && merged.pregnancy == null) {
      merged.pregnancy = dossier.pregnancy as Record<string, unknown>;
    }
    return merged as Patient;
  }, [dossier]);

  useDocumentTitle(
    `DEEPECHO - ${t('modals.editPatient.title', { defaultValue: 'Edit Patient' })}`
  );

  const handlePatientUpdated = () => {
    refetch();
    navigate(returnTo);
  };

  if (error) {
    const message = getLoadErrorMessage(error, t('notifications.error'));
    return (
      <div className="bg-card-color-200 dark:bg-gray-900 p-6">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4">
          <ErrorDisplay message={message} />
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => navigate(returnTo)}>
              {t('buttons.cancel')}
            </Button>
            <Button onClick={() => refetch()}>
              {tPatient('retry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PatientFormPageShell
      title={t('modals.editPatient.title')}
      subtitle={t('pageSubtitle')}
      cancelLabel={t('buttons.cancel')}
      submitLabel={t('modals.editPatient.form.submitButton')}
      savingLabel={`${t('buttons.save')}...`}
      returnTo={returnTo}
      isSubmitting={isFormSubmitting}
      isLoading={isLoading || !patientToEdit}
      onSubmit={() => editFormRef.current?.requestSubmit()}
    >
      {patientToEdit && (
        <AddPatientForm
          ref={editFormRef}
          patientToEdit={patientToEdit}
          dossierForEdit={{
            pregnancy: dossier.pregnancy ?? null,
            family_history: dossier.family_history ?? null,
            patient_medical_history: dossier.patient_medical_history ?? null,
            prior_deliveries: dossier.prior_deliveries ?? null,
          }}
          onPatientUpdated={handlePatientUpdated}
          onSubmittingChange={setIsFormSubmitting}
        />
      )}
    </PatientFormPageShell>
  );
};

export default EditPatientPage;
